import {
  collection,
  doc,
  getDocs,
  runTransaction,
  type Firestore,
} from 'firebase/firestore';

import type { SiteLayer } from '../../types/site';
import type { LandGateAffectedSection } from '../../types/site';
import {
  dirtySectionsForLandGateReviewChanges,
  planLandGateDirtyAcknowledgement,
  type LandGateDirtyAcknowledgementPlan,
} from '../../lib/siteLayers/landGatePromotion';
import { stripUndefined } from '../../lib/firestore/stripUndefined';
import { assertSiteLayersRevision } from '../../lib/siteLayers/siteLayerRevision';
import { assertSiteLayerParentBudget, projectSiteLayerCompatibilityMirror } from '../../lib/siteLayers/siteLayerCompatibilityMirror';
export { SITE_LAYER_LEGACY_MIRROR_MAX_BYTES } from '../../lib/siteLayers/siteLayerCompatibilityMirror';
import {
  isManagedGenerationImpactLayer,
  preserveManagedGenerationImpactLayers,
} from '../../lib/siteLayers/managedGenerationImpactLayers';

/** Keep layer-set writes below Firestore's 500-operation transaction ceiling. */
export const SITE_LAYER_MAX_COUNT = 450;
export const SITE_LAYER_MAX_VERTICES = 100_000;
export const SITE_LAYER_MAX_DOCUMENT_BYTES = 750 * 1024;
export const SITE_LAYER_MAX_TOTAL_BYTES = 16 * 1024 * 1024;

const FIRESTORE_TRANSACTION_WRITE_LIMIT = 500;

function serializedBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}

function validateLayerSet(layers: readonly SiteLayer[]): void {
  if (layers.length > SITE_LAYER_MAX_COUNT) {
    throw new Error(`A site may contain at most ${SITE_LAYER_MAX_COUNT} map layers.`);
  }

  let totalVertices = 0;
  let totalBytes = 0;
  for (const layer of layers) {
    totalVertices += layer.geometry.coordinates.length;
    if (totalVertices > SITE_LAYER_MAX_VERTICES) {
      throw new Error(`Site map layers may contain at most ${SITE_LAYER_MAX_VERTICES.toLocaleString()} vertices in total.`);
    }

    const bytes = serializedBytes(layer);
    if (bytes > SITE_LAYER_MAX_DOCUMENT_BYTES) {
      throw new Error(`Map layer "${layer.name}" is too large to persist safely; simplify its geometry before saving.`);
    }
    totalBytes += bytes;
    if (totalBytes > SITE_LAYER_MAX_TOTAL_BYTES) {
      throw new Error('Site map layers exceed the 16 MB persistence budget; remove or simplify layers before saving.');
    }
  }
}

export interface PersistSiteLayersResult {
  layers: SiteLayer[];
  revision: number;
  added: number;
  updated: number;
  deleted: number;
}

function normalizedLayerId(layer: SiteLayer): string {
  return layer.id?.trim() || crypto.randomUUID();
}

export function withLayerTimestamps(
  layer: SiteLayer,
  nowIso: string,
  existing?: SiteLayer,
): SiteLayer {
  const createdAt = existing?.createdAt ?? layer.createdAt ?? nowIso;
  return {
    ...layer,
    id: normalizedLayerId(layer),
    createdAt,
    updatedAt: nowIso,
  };
}

export async function listSiteLayers(
  db: Firestore,
  uid: string,
  siteId: string,
): Promise<SiteLayer[]> {
  const snap = await getDocs(collection(db, `users/${uid}/sites/${siteId}/layers`));
  return snap.docs.map((d) => {
    const data = d.data() as SiteLayer;
    return {
      ...data,
      // Subcollection document identity is canonical; payload ids are
      // denormalized and may lag a migration/repair.
      id: d.id,
    };
  });
}

export async function saveSiteLayersBatch(params: {
  db: Firestore;
  uid: string;
  siteId: string;
  nextLayers: SiteLayer[];
  prevLayers: SiteLayer[];
  /** Layer-set revision observed when the editor draft was opened. */
  expectedRevision: number;
  previousDirtySections?: LandGateAffectedSection[];
  /** Active server publication owns the parent compatibility mirror. */
  hasGenerationImpactLayoutHead?: boolean;
}): Promise<PersistSiteLayersResult> {
  const {
    db,
    uid,
    siteId,
    nextLayers,
    prevLayers,
    hasGenerationImpactLayoutHead = false,
  } = params;
  const nowIso = new Date().toISOString();
  const prevById = new Map(prevLayers.map((layer) => [layer.id, layer]));
  // Generation & Impact publications own their materialized map layers.
  // Full-array editor saves are intentionally unable to mutate or delete
  // those rows; the publication/activation workflow is the sole writer.
  const protectedNextLayers = preserveManagedGenerationImpactLayers(prevLayers, nextLayers);
  // Firestore rejects `undefined` field values (the web SDK is initialised
  // WITHOUT ignoreUndefinedProperties). A KMZ layer with no `description`
  // carries `description: undefined`, which threw on batch.set. Scrub each
  // stamped layer once here so every downstream write (per-layer doc, bounded
  // legacy mirror candidates, and the returned value) is undefined-free.
  const siteRef = doc(db, `users/${uid}/sites`, siteId);
  let discoveredCanonicalIds: string[] = [];
  try {
    const existingCanonical = await listSiteLayers(db, uid, siteId);
    discoveredCanonicalIds = existingCanonical.map((layer) => layer.id).filter(Boolean);
  } catch {
    // Transaction reads below remain authoritative.
  }
  return runTransaction(db, async (transaction): Promise<PersistSiteLayersResult> => {
    // The site mirror is the transaction conflict point shared with the
    // KMZ-link patcher. A stale writer from another session therefore retries
    // and preserves the latest transaction-owned canvas link.
    const siteSnapshot = await transaction.get(siteRef);
    if (!siteSnapshot.exists()) {
      throw new Error(`Cannot update layers: site ${siteId} no longer exists.`);
    }
    const siteData = siteSnapshot.data() as Record<string, unknown> & {
      siteLayers?: unknown;
      siteLayerCount?: unknown;
      siteLayersMirrorComplete?: unknown;
      siteLayersRevision?: unknown;
      siteLayersCanonicalVersion?: unknown;
      siteEvidenceDirtySections?: LandGateAffectedSection[];
      generationImpactLayoutHead?: unknown;
    };
    const liveRevision = assertSiteLayersRevision(
      params.expectedRevision,
      siteData.siteLayersRevision,
    );
    const mirroredLayers = Array.isArray(siteData.siteLayers)
      ? siteData.siteLayers as SiteLayer[]
      : [];
    // Once canonical version 1 is established, a bounded mirror can omit any
    // ordinary row. Read its canonical documents in this transaction so a
    // stale editor cannot overwrite an omitted Canvas link. Unmigrated sites
    // retain the transaction-owned mirror as their authority; older layer
    // documents must not replace a newer legacy mirror link.
    const authoritativeById = new Map(
      prevLayers.map((layer) => [layer.id, layer]),
    );
    for (const layer of mirroredLayers) authoritativeById.set(layer.id, layer);
    const previousIds = [...new Set([
      ...prevLayers.map(layer => layer.id),
      ...discoveredCanonicalIds,
    ])];
    const previousDocs = await Promise.all(previousIds.map(id => transaction.get(
      doc(db, `users/${uid}/sites/${siteId}/layers`, id),
    )));
    previousDocs.forEach((snapshot, index) => {
      const id = previousIds[index]!;
      if (siteData.siteLayersCanonicalVersion !== 1) return;
      if (snapshot.exists()) {
        const canonicalRow = { ...(snapshot.data() as SiteLayer), id };
        if (!prevById.has(id) && !isManagedGenerationImpactLayer(canonicalRow)) {
          throw new Error(
            `Canonical site layer ${id} is missing from the editor baseline. Reload the site layers before saving.`,
          );
        }
        authoritativeById.set(id, canonicalRow);
      } else if (prevById.has(id)) {
        throw new Error(`Site layer ${id} disappeared. Reload the layers before saving.`);
      }
    });
    if (
      siteData.siteLayersCanonicalVersion === 1
      && typeof siteData.siteLayerCount === 'number'
      && Number.isInteger(siteData.siteLayerCount)
      && siteData.siteLayerCount >= 0
      && authoritativeById.size < siteData.siteLayerCount
    ) {
      throw new Error(
        `Canonical site layer baseline is incomplete (${authoritativeById.size} of ${siteData.siteLayerCount} layers loaded). Wait for layer hydration or reload before saving.`,
      );
    }
    const authoritativeLayers = [...authoritativeById.values()];

    // A publish may have landed after this editor loaded. Reconcile the live
    // revision-guarded mirror as well as the editor snapshot so a stale full-
    // array save cannot classify a newly published row as a deletion.
    const protectedAuthoritativeNextLayers = preserveManagedGenerationImpactLayers(
      authoritativeLayers,
      protectedNextLayers,
    );
    const nextStamped = protectedAuthoritativeNextLayers.map((layer) => {
      const authoritative = authoritativeById.get(layer.id);
      if (authoritative && isManagedGenerationImpactLayer(authoritative)) {
        return stripUndefined(authoritative);
      }
      if (isManagedGenerationImpactLayer(layer)) {
        throw new Error('Generation & Impact layout layers are server-managed; publish the layout instead of saving them as ordinary map layers.');
      }
      const { linkedCanvasNodeId: _staleIncomingLink, ...withoutIncomingLink } = layer;
      return stripUndefined(withLayerTimestamps({
        ...withoutIncomingLink,
        ...(authoritative?.linkedCanvasNodeId
          ? { linkedCanvasNodeId: authoritative.linkedCanvasNodeId }
          : {}),
      } as SiteLayer, nowIso, authoritative ?? prevById.get(layer.id)));
    });
    validateLayerSet(nextStamped);
    const nextById = new Map(nextStamped.map((layer) => [layer.id, layer]));
    const deletedLayers = authoritativeLayers.filter(previous => !nextById.has(previous.id));
    // The trusted publisher is the sole writer for managed materializations.
    // Retain these rows in the canonical union returned to the store, but do
    // not count or issue even a byte-identical client write for them.
    const clientWritableNextLayers = nextStamped.filter(
      layer => !isManagedGenerationImpactLayer(layer),
    );
    const clientDeletableLayers = deletedLayers.filter(
      layer => !isManagedGenerationImpactLayer(layer),
    );
    if (clientWritableNextLayers.length + clientDeletableLayers.length + 1 > FIRESTORE_TRANSACTION_WRITE_LIMIT) {
      throw new Error(
        'This replacement needs more than 500 atomic map-layer writes. Save deletions separately, then retry the import.',
      );
    }

    const promotionDirty = dirtySectionsForLandGateReviewChanges(
      authoritativeLayers,
      nextStamped,
    );
    const dirtySections = [...new Set([
      ...(siteData.siteEvidenceDirtySections ?? params.previousDirtySections ?? []),
      ...promotionDirty,
    ])].sort();
    const generationPublisherOwnsMirror = hasGenerationImpactLayoutHead
      || siteData.generationImpactLayoutHead != null;
    const parentPatch = {
      updatedAt: nowIso,
      siteLayersCanonicalVersion: 1 as const,
      siteLayersRevision: liveRevision + 1,
      ...(promotionDirty.length > 0
        ? { siteEvidenceDirtySections: dirtySections, siteEvidenceDirtyAt: nowIso }
        : {}),
    };
    const mirrorPatch = generationPublisherOwnsMirror ? {} : projectSiteLayerCompatibilityMirror({
      liveSite: siteData,
      parentPatch,
      canonicalLayers: nextStamped,
    });
    if (generationPublisherOwnsMirror) assertSiteLayerParentBudget(siteData, parentPatch);
    // Validate the exact parent projection before issuing any writes. The
    // transaction commits the complete canonical rows and bounded mirror
    // together; a budget failure cannot publish a partial replacement.
    let added = 0;
    let updated = 0;
    let deleted = 0;
    for (const layer of clientWritableNextLayers) {
      if (authoritativeById.has(layer.id)) updated += 1;
      else added += 1;
      transaction.set(doc(db, `users/${uid}/sites/${siteId}/layers`, layer.id), layer);
    }
    for (const previous of clientDeletableLayers) {
      deleted += 1;
      transaction.delete(doc(db, `users/${uid}/sites/${siteId}/layers`, previous.id));
    }
    transaction.set(siteRef, { ...parentPatch, ...mirrorPatch }, { merge: true });

    return { layers: nextStamped, revision: liveRevision + 1, added, updated, deleted };
  });
}

/**
 * Acknowledge reviewed LandGate evidence only when the transaction sees the
 * same layer-review state that was projected into the persisted SED. The site
 * mirror is used strictly as the CAS token maintained by saveSiteLayersBatch;
 * canonical projection always reads the `/layers` subcollection.
 */
export async function acknowledgeReviewedLandGateEvidence(params: {
  db: Firestore;
  uid: string;
  siteId: string;
  consumedInputSignature: string;
  acknowledgedSections: readonly LandGateAffectedSection[];
}): Promise<LandGateDirtyAcknowledgementPlan> {
  const siteRef = doc(params.db, `users/${params.uid}/sites`, params.siteId);
  return runTransaction(params.db, async transaction => {
    const snapshot = await transaction.get(siteRef);
    if (!snapshot.exists()) throw new Error(`Cannot acknowledge evidence: site ${params.siteId} no longer exists.`);
    const siteData = snapshot.data() as {
      siteLayers?: unknown;
      siteEvidenceDirtySections?: LandGateAffectedSection[];
      siteEvidenceDirtyAt?: string | null;
    };
    // Missing/non-array mirrors predate the atomic layer writer and are not a
    // trustworthy CAS token. Fail closed and leave every dirty section intact.
    if (!Array.isArray(siteData.siteLayers)) {
      return {
        signatureMatched: false,
        clearedSections: [],
        remainingSections: [...new Set(siteData.siteEvidenceDirtySections ?? [])].sort(),
      };
    }
    const plan = planLandGateDirtyAcknowledgement({
      liveLayers: siteData.siteLayers as SiteLayer[],
      consumedInputSignature: params.consumedInputSignature,
      dirtySections: siteData.siteEvidenceDirtySections,
      acknowledgedSections: params.acknowledgedSections,
    });
    if (plan.clearedSections.length === 0) return plan;
    const nowIso = new Date().toISOString();
    transaction.set(siteRef, {
      siteEvidenceDirtySections: plan.remainingSections,
      siteEvidenceDirtyAt: plan.remainingSections.length > 0
        ? siteData.siteEvidenceDirtyAt ?? nowIso
        : null,
      updatedAt: nowIso,
    }, { merge: true });
    return plan;
  });
}
